import React, { Component } from 'react';
import partial from 'lodash/partial';
import flow from 'lodash/flow';
import isEqual from 'lodash/isEqual';

import Loading from '../Loading';
import EventMapMapbox from './component';
import { clusterPin, hoverClusterPin } from './styles.module.css';
import { withBrowserInfo } from '../../hocs';
import { SEGMENT_EVENTS } from '../../utils/tracking';

import {
  withSearchInfo,
  withTracking,
} from '../../views/CategoryPage/provider';

// formats viewport values into mapbox viewport bounds
const getBounds = ({
  viewportNorthEastLat,
  viewportNorthEastLong,
  viewportSouthWestLat,
  viewportSouthWestLong,
}) => {
  if (
    viewportNorthEastLat &&
    viewportNorthEastLong &&
    viewportSouthWestLat &&
    viewportSouthWestLong
  ) {
    return [
      // sw
      [parseFloat(viewportSouthWestLong), parseFloat(viewportSouthWestLat)],
      // ne
      [parseFloat(viewportNorthEastLong), parseFloat(viewportNorthEastLat)],
    ];
  }
  return null;
};

class EventMapMapboxContainer extends Component {
  constructor(props) {
    super(props);
    this.ReactMapboxGl = undefined;
    this.Map = undefined;

    // unwrapped mapbox-gl map js object -- bypasses react props.
    this.unwrappedMap = undefined;

    // Tracking Variables
    this.zoom = undefined;
    this.center = undefined;
    this.bounds = undefined;

    // conditionally loads mapbox on client
    if (__CLIENT__) import('react-mapbox-gl').then(this.setMap);

    // Bounding Box
    const {
      viewportNorthEastLat,
      viewportNorthEastLong,
      viewportSouthWestLat,
      viewportSouthWestLong,
    } = this.props;

    const fitBounds = getBounds({
      viewportNorthEastLat,
      viewportNorthEastLong,
      viewportSouthWestLat,
      viewportSouthWestLong,
    });

    this.state = {
      // used to updated isMapMoved State
      // eslint-disable-next-line react/no-unused-state
      events: undefined,
      selectedEvent: undefined,
      scriptjsLoaderEnabled: false,
      isLoading: true,
      prevBoundsProp: fitBounds,
      fitBounds,
      prevCenterProp: this.props.center,
      center: this.props.center || undefined,
      zoom: [9],
      isMapMoved: false,
    };
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (
      nextProps.center &&
      !isEqual(nextProps.center, prevState.prevCenterProp)
    ) {
      return {
        events: nextProps.events,
        prevCenterProp: nextProps.center,
        prevBoundsProp: undefined,
        center: nextProps.center,
        zoom: [9],
        isLoading: false,
        isMapMoved: false,
      };
    }

    const {
      viewportNorthEastLat,
      viewportNorthEastLong,
      viewportSouthWestLat,
      viewportSouthWestLong,
    } = nextProps;

    const nextFitBounds = getBounds({
      viewportNorthEastLat,
      viewportNorthEastLong,
      viewportSouthWestLat,
      viewportSouthWestLong,
    });

    if (nextFitBounds && !isEqual(nextFitBounds, prevState.prevBoundsProp)) {
      return {
        fitBounds: nextFitBounds,
        prevBoundsProp: nextFitBounds,
        prevCenterProp: undefined,
        events: nextProps.events,
        isLoading: false,
        isMapMoved: false,
      };
    }

    if (nextProps?.events === prevState?.events) return null;

    if (nextProps.events) {
      return { events: nextProps.events, isLoading: false, isMapMoved: false };
    }

    return null;
  }

  componentDidUpdate(prevProps) {
    const { selectedEventId: oldSelectedEventId } = prevProps;
    const { selectedEventId: newSelectedEventId } = this.props;
    if (oldSelectedEventId !== newSelectedEventId) {
      this.focusOnEvent(newSelectedEventId);
    }
  }

  componentWillUnmount() {
    // Teardown Tracking Map Event Listeners
    if (this.unwrappedMap) {
      this.unwrappedMap.off('movestart', this.setInitialMapStateTrackingVars);
      this.unwrappedMap.off('moveend', this.trackMapAreaChanges);
    }
  }

  onMapClick = () => {
    if (this.props.onEventSelect) this.props.onEventSelect(undefined);
  };

  onSearchMap = () => {
    this.setState({ isLoading: true, selectedEvent: undefined });
    const { onNewSearch } = this.props;
    if (!this.unwrappedMap) return null;
    const bounds = this.unwrappedMap.getBounds();

    const [
      [viewportSouthWestLong, viewportSouthWestLat],
      [viewportNorthEastLong, viewportNorthEastLat],
    ] = bounds.toArray();

    onNewSearch({
      viewportNorthEastLat,
      viewportNorthEastLong,
      viewportSouthWestLat,
      viewportSouthWestLong,
    });
  };

  // This function overrides zoomClick component default for tracking purposes
  onZoomClick = (map, zoomDiff) => {
    const coordinates = map.getCenter();
    const zoom = map.getZoom();
    map.zoomTo(zoom + zoomDiff);
    this.props.handleTracking('zoom_click', { coordinates, zoom, zoomDiff });
  };

  onClusterClick = (_coordinates, pointCount) => {
    const [lng, lat] = _coordinates;
    const coordinates = { lat, lng };
    this.props.handleTracking('cluster_click', { coordinates, pointCount });
    this.setState({ selectedEvent: undefined });

    // CUSTOM ON CLICK -- Example of how using UnwrappedMap
    // Replaced with zoomOnClick in ClusterProp
    // const increaseZoom = (prevState) => {
    //   if (pointCount > 1) {
    //     const prevZoom =
    //       // checks zoom of map if user zooms out on their own before infering last clicked
    //       (this.unwrappedMap && this.unwrappedMap.getZoom()) ||
    //       prevState.zoom[0];
    //     return [Math.min(prevZoom + 1, 15)];
    //   }
    //   return [8];
    // };
    // this.setState((prevState) => ({
    //   zoom: increaseZoom(prevState),
    //   center: coordinates,
    //   event,
    // }));
  };

  setMap = (ReactMapboxGl) => {
    this.ReactMapboxGl = ReactMapboxGl;
    this.Map = this.ReactMapboxGl.Map({
      accessToken: MAPBOX_API_KEY,
      maxZoom: 16,
    });

    this.setState({
      scriptjsLoaderEnabled: true,
    });

    const { mapLoaded } = this.props;
    mapLoaded();
  };

  setInitialMapStateTrackingVars = () => {
    this.zoom = this.unwrappedMap.getZoom();
    this.center = this.unwrappedMap.getCenter();
    this.bounds = this.unwrappedMap.getBounds();
  };

  getUnwrappedMap = (mapboxGlLib) => {
    this.unwrappedMap = mapboxGlLib;

    // set tracking event listeners
    this.unwrappedMap.on('movestart', this.setInitialMapStateTrackingVars);
    this.unwrappedMap.on('moveend', this.trackMapAreaChanges);
  };

  trackMapAreaChanges = () => {
    const prevZoom = this.zoom;
    const prevCenter = this.center;
    const prevBounds = this.bounds;
    const newBounds = this.unwrappedMap.getBounds();
    const newCenter = this.unwrappedMap.getCenter();
    const newZoom = this.unwrappedMap.getZoom();

    this.props.handleTracking(
      'change_map_area',
      { prevZoom, prevCenter, prevBounds, newBounds, newCenter, newZoom },
      SEGMENT_EVENTS.MAP_MOVED,
    );
    this.setState({ isMapMoved: true });
  };

  // Note: currently the selected event state is handled at the parent level.
  // to support custom scroll to search card on map mobile view. @dion
  focusOnEvent = (eventId) => {
    if (!eventId) return this.setState({ selectedEvent: undefined });

    const event = this.props.events[
      this.props.events.findIndex((_event) => _event.id === eventId)
    ];

    this.setState({
      selectedEvent: event,
    });
  };

  clusterMarker = (
    ReactMapboxGl,
    hoverEventId,
    coordinates,
    pointCount,
    getLeaves,
  ) => {
    let hoveredLeaf = [];
    if (hoverEventId) {
      hoveredLeaf = getLeaves().filter(
        (child) => child.key.toString() === hoverEventId.toString(),
      );
    }

    return (
      <ReactMapboxGl.Marker
        key={coordinates.toString()}
        coordinates={coordinates}
        className={hoveredLeaf.length > 0 ? hoverClusterPin : clusterPin}
        onClick={partial(this.onClusterClick, coordinates, pointCount)}
        anchor="bottom"
      >
        <span>{pointCount}</span>
      </ReactMapboxGl.Marker>
    );
  };

  render() {
    const {
      events,
      loading: loadingProp,
      selectedEventId,
      hoverEventId,
      onEventSelect,
      isMobile,
      mapStyle,
    } = this.props;

    const loading = loadingProp ?? this.state.isLoading;

    if (!this.state.scriptjsLoaderEnabled || !this.ReactMapboxGl) {
      return <Loading />;
    }

    const { fitBounds, center, zoom } = this.state;

    const componentProps = {
      selectedEvent: this.state.selectedEvent,
      selectedEventId,
      isMapMoved: this.state.isMapMoved,
      isMobile,
      fitBounds,
      center,
      zoom,
      loading,
      events: loading ? [] : events,
      onMarkerClick: onEventSelect,
      clusterMarker: partial(
        this.clusterMarker,
        this.ReactMapboxGl,
        hoverEventId,
      ),
      getUnwrappedMap: this.getUnwrappedMap,
      ReactMapboxGl: this.ReactMapboxGl,
      Map: this.Map,
      onSearchMap: this.onSearchMap,
      onZoomClick: this.onZoomClick,
      hoverEventId,
      mapStyle,
      onMapClick: this.onMapClick,
    };

    return <EventMapMapbox {...componentProps} />;
  }
}

EventMapMapboxContainer.defaultProps = {
  onNewSearch: () => {},
};

export default flow(
  withBrowserInfo,
  withSearchInfo,
  withTracking('map'),
)(EventMapMapboxContainer);
